import { isZeroQuotePlaceholder } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'

import { calculateInsuranceEstimateCents } from '@/helpers/calculateInsuranceEstimate'

type AutoPayTarget = {
  payForId: string
  payForType: 'contract' | 'listing'
}

/**
 * Auto-pay is disabled when:
 * 1. Listing doesn't have autoPay enabled, OR
 * 2. Quote is zero/placeholder (shipping cost not yet available)
 *
 * @param listing - The auction listing details
 * @param selectedQuote - The selected shipping quote (can be null or placeholder)
 * @returns true if auto-pay should be enabled, false otherwise
 */
export const getHasAutoPay = (
  listing: OrchestratedAuctionListingDetails,
  selectedQuote?: OrchestratedQuote | null
): boolean => {
  if (!selectedQuote) {
    return false
  }

  const isZeroQuoteValue = isZeroQuotePlaceholder(selectedQuote)

  return !!listing.autoPay && !isZeroQuoteValue
}

/**
 * Whether the Confirm Max Bid modal must be withheld because a shipping quote
 * is required but generation has settled without one (e.g. no eligible address)
 * — BP-2262. While a quote is still generating the caller shows a loading state
 * instead; a zero-rate "ship later" quote is truthy, so bidding is allowed.
 * Seller-collects listings never need a shipping quote (they invoice
 * separately), so they are never blocked. Shared by the modal (renders nothing)
 * and QuoteGate (renders the shipping error) so both surfaces gate identically.
 */
export const isBidBlockedOnShippingQuote = ({
  isActiveBuyer,
  isSellerCollects,
  quoteLoading,
  selectedQuote,
}: {
  isActiveBuyer: boolean
  isSellerCollects: boolean
  quoteLoading: boolean
  selectedQuote?: OrchestratedQuote | null
}): boolean =>
  isActiveBuyer && !isSellerCollects && !quoteLoading && !selectedQuote

export const getContractAutoPayId = (
  listing: OrchestratedAuctionListingDetails
): string | null => {
  // contractId is not yet in the generated OrchestratedAuctionListingDetails
  // type. It is the canonical id used by fulfillment listings and order-service
  // AutoPay scheduling, while listing.contract._id can point at a related
  // contract detail document. Remove this cast once the generated type includes
  // contractId.
  const contractId = listing.contractId

  return contractId ?? listing.contract?._id ?? null
}

export const isContractListing = (
  listing: OrchestratedAuctionListingDetails
): boolean =>
  listing.listingType === 'CONTRACT' || getContractAutoPayId(listing) !== null

export const getAutoPayTarget = (
  listing: OrchestratedAuctionListingDetails
): AutoPayTarget | null => {
  const contractAutoPayId = getContractAutoPayId(listing)
  const isContract =
    listing.listingType === 'CONTRACT' || contractAutoPayId !== null

  if (!isContract) {
    return { payForId: listing._id, payForType: 'listing' }
  }

  if (!contractAutoPayId) return null

  return { payForId: contractAutoPayId, payForType: 'contract' }
}

/**
 * calculate estimated total amount in cents, including the parcel shipping
 * insurance estimate when the quote offers insurance
 * @param bidAmountInDollars - The bid amount in dollars
 * @param selectedQuote - The selected shipping quote (can be null)
 * @param bStockFeeInCents - The B-Stock fee in cents
 * @returns estimated total amount in cents, or 0 if quote is zero/placeholder
 */
export const calculateEstimatedTotalPayment = (
  bidAmountInDollars: number,
  selectedQuote: OrchestratedQuote | null | undefined,
  bStockFeeInCents: number
): number => {
  if (!selectedQuote) {
    return 0
  }

  const isZeroQuoteValue = isZeroQuotePlaceholder(selectedQuote)

  if (isZeroQuoteValue) {
    return 0
  }

  const bidCents = bidAmountInDollars * 100
  const insuranceCents =
    selectedQuote.insurance?.available === true
      ? calculateInsuranceEstimateCents(selectedQuote.insurance, bidCents)
      : 0

  return bidCents + selectedQuote.totalPrice + bStockFeeInCents + insuranceCents
}
